import { BlockUI } from 'primereact/blockui';
import { Checkbox } from 'primereact/checkbox';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import React, { useRef, useState } from "react";
import { useQuery } from 'react-query';
import PageHeading from "../../../components/PageHeading";
import authApi from "../../../service/IdApi";
import LazyState from '../../../utils/LazyState';
import PageLoadError from '../../../components/PageLoadError';
import { useAppContext } from "../../../context/appContext";
import TokenService from '../../../utils/TokenService';

const ImportCompanyData = () => {
    const { switchUnclaimedProfile } = useAppContext();
    const [stateSelection, setStateSelection] = useState('FL');
    const [isImporting, setIsImporting] = useState(false);
    const [importMessage, setImportMessage] = useState(null);
    const [importMessageType, setImportMessageType] = useState('success');
    const [companyData, setCompanyData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const fileUploadRef = useRef(null);
    const [useKeywordSearch, setUseKeywordSearch] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [lazyState, setLazyState] = useState(() => {
        return LazyState.getImportCompanyDataLazyState();
    });
    const [timer, setTimer] = useState(null);
    const menu = useRef(null);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [showGenerateProfileDialog, setShowGenerateProfileDialog] = useState(false);
    const [friendlyName, setFriendlyName] = useState('');
    const [idealDomainLC, setIdealDomainLC] = useState('');
    const [friendlyIdealDomain, setFriendlyIdealDomain] = useState('');
    const [friendlyCity, setFriendlyCity] = useState('');
    const [isCheckingDomain, setIsCheckingDomain] = useState(false);
    const [domainSearchResult, setDomainSearchResult] = useState(null);
    const [isGeneratingProfile, setIsGeneratingProfile] = useState(false);
    const [rowActions, setRowActions] = useState([]);
    const [openingProfileFor, setOpeningProfileFor] = useState(null);

    const { isLoading, isError, refetch } = useQuery(["ImportCompanyData", lazyState], async () => {
        let body = {
            first: lazyState.first,
            rows: lazyState.rows,
            sortField: lazyState.sortField,
            sortOrder: lazyState.sortOrder,
            searchText: searchText,
            useKeywordSearch: useKeywordSearch,
        }

        const response = await authApi.post("admin/importedCompanies", body);
        setCompanyData(response.data?.companies || []);
        setTotalRecords(response.data?.totalRecords || 0);
        return response;
    });

    const handleImport = async (event) => {
        if (!event.files || !stateSelection) return;
        setIsImporting(true);
        setImportMessage(null);

        const formData = new FormData();
        formData.append("state", stateSelection);
        formData.append("file", event.files[0]);

        try {
            // Replace this URL with the actual API endpoint
            await authApi.post("admin/import-company-data", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            setImportMessage("File imported successfully!");
            setImportMessageType("success");

            //This will cause a refresh of the datatable
            setLazyState(LazyState.resetLazyState(lazyState));
        } catch (error) {
            setImportMessage("Import failed: " + (error?.response?.data?.msg || error.message));
            setImportMessageType("danger");
        } finally {
            setIsImporting(false);
            fileUploadRef.current.clear();
        }
    };


    const updateUseKeywordSearch = (event) => {
        setUseKeywordSearch(event.checked);
        setLazyState(LazyState.resetLazyState(lazyState));
    };

    const updateSearchText = (event) => {
        if (searchText !== event.target.value) {
            setSearchText(event.target.value);
            if (timer) {
                clearTimeout(timer);
            }

            const newTimer = setTimeout(() => {
                setLazyState(LazyState.resetLazyState(lazyState));
            }, 500);

            setTimer(newTimer);
        }
    };

    const clearSearchText = () => {
        if (timer) {
            clearTimeout(timer);
            setTimer(null);
        }

        setSearchText('');
        setLazyState(LazyState.resetLazyState(lazyState));
    };

    const onPage = (event) => {
        setLazyState(event);
    };

    const onSort = (event) => {
        setLazyState(event);
    };

    const pageHeader = (
        <PageHeading pageTitle="Admin - Import Company Data">
        </PageHeading>
    );


    const filedOnDateBodyTemplate = (rowData) => {
        if (!rowData.filedOnDate) return '';
        const date = new Date(rowData.filedOnDate);
        return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    };

    const addressBodyTemplate = (rowData) => {
        return (
            <>
                <span className="white-space-nowrap">
                    {rowData.address1}
                    {rowData.address2 && <br />}
                    {rowData.address2}
                </span>
                <span>
                    {rowData.city && <br />}
                    {rowData.city}
                    {rowData.city && <span>, </span>}
                    {rowData.state} {rowData.zip}
                </span>
            </>

        );
        return rowData.address1;
    };

    // Generate friendly company name: lowercase, trim, remove business suffixes, capitalize first letter of each word
    const generateFriendlyName = (companyName) => {
        if (!companyName) return '';

        // Business suffixes to remove
        const suffixes = ['llc', 'inc', 'corp', 'ltd', 'l.l.c.', 'l.l.c', 'inc.', 'corp.', 'ltd.', 'incorporated', 'corporation', 'limited'];

        let name = companyName.toLowerCase().trim();

        // Remove suffixes from the end
        for (const suffix of suffixes) {
            const regex = new RegExp(`\\s*,\\s*${suffix.replace(/\./g, '\\.')}\\s*$|\\s+${suffix.replace(/\./g, '\\.')}\\s*$`, 'i');
            name = name.replace(regex, '');
        }

        // Capitalize first letter of each word
        name = name.split(' ').map(word => {
            if (word.length === 0) return word;
            return word.charAt(0).toUpperCase() + word.slice(1);
        }).join(' ');

        return name;
    };

    // Generate domain from friendly name: strip non-domain characters
    const generateDomainFromName = (friendlyName) => {
        if (!friendlyName) return '';

        // Remove non-domain supporting characters (hyphens, spaces, apostrophes, etc.)
        let domain = friendlyName.toLowerCase()
            .replace(/[^a-z0-9]/g, '') // Remove all non-alphanumeric characters
            .trim();

        return domain + '.com';
    };

    // Generate friendly city name: lowercase and capitalize first letter of each word
    const generateFriendlyCity = (city) => {
        if (!city) return '';

        return city.toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Generate mixed case domain for friendlyIdealDomain
    // Example: "Steve's Landscaping" -> "www.StevesLandscaping.com"
    const generateFriendlyDomain = (friendlyName, domainLC) => {
        if (!friendlyName || !domainLC) return '';

        // Split friendly name into words, capitalize first letter of each word
        // Remove all non-alphanumeric characters but preserve word capitalization
        const words = friendlyName.split(/\s+/).filter(word => word.length > 0);
        let result = '';

        for (const word of words) {
            // Remove special characters (apostrophes, etc.) but keep letters and numbers
            const cleanWord = word.replace(/[^a-zA-Z0-9]/g, '');
            if (cleanWord.length > 0) {
                // Capitalize first letter, lowercase the rest
                result += cleanWord.charAt(0).toUpperCase() + cleanWord.slice(1).toLowerCase();
            }
        }

        return 'www.' + result + '.com';
    };

    const checkDomainAvailability = async (domain) => {
        if (!domain || !domain.trim()) return;

        setIsCheckingDomain(true);
        setDomainSearchResult(null);

        try {
            const response = await authApi.post("admin/profile/custom-domain/search", {
                domain: domain.trim()
            });
            setDomainSearchResult(response.data);
        } catch (error) {
            console.error("Domain search error:", error);
            setDomainSearchResult({ available: false, error: error.response?.data?.message || error.message });
        } finally {
            setIsCheckingDomain(false);
        }
    };

    const showActionMenu = (e, rowData) => {
        const actions = [];

        // Only show "Generate Profile" if profile is not already created
        if (!rowData.companyMetaData?.profileCreated) {
            actions.push({
                label: "Generate Profile",
                icon: "pi pi-plus",
                command: () => {
                    openGenerateProfileDialog(rowData);
                },
            });
        }

        setRowActions(actions);
        setSelectedCompany(rowData);
        menu.current.toggle(e);
    };

    const updateFriendlyDomainFromIdealDomain = (newIdealDomain, currentFriendlyName) => {
        if (!newIdealDomain || !currentFriendlyName) return;
        const newFriendlyDomain = generateFriendlyDomain(currentFriendlyName, newIdealDomain);
        setFriendlyIdealDomain(newFriendlyDomain);
    };

    const openGenerateProfileDialog = async (rowData) => {
        setSelectedCompany(rowData);

        // Generate friendly name
        const generatedFriendlyName = generateFriendlyName(rowData.companyName);
        setFriendlyName(generatedFriendlyName);

        // Generate domain
        const generatedDomain = generateDomainFromName(generatedFriendlyName);
        setIdealDomainLC(generatedDomain);

        // Generate friendly domain
        const generatedFriendlyDomain = generateFriendlyDomain(generatedFriendlyName, generatedDomain);
        setFriendlyIdealDomain(generatedFriendlyDomain);

        // Generate friendly city
        const generatedFriendlyCity = generateFriendlyCity(rowData.city);
        setFriendlyCity(generatedFriendlyCity);

        setShowGenerateProfileDialog(true);
        setDomainSearchResult(null);

        // Check domain availability
        if (generatedDomain) {
            await checkDomainAvailability(generatedDomain);
        }
    };

    const handleDomainSearch = async () => {
        if (idealDomainLC && idealDomainLC.trim()) {
            await checkDomainAvailability(idealDomainLC);
        }
    };

    const handleGenerateProfile = async () => {
        if (!selectedCompany) return;

        setIsGeneratingProfile(true);

        try {
            const payload = {
                documentNumber: selectedCompany.documentNumber,
                friendlyName: friendlyName,
                idealDomainLC: idealDomainLC,
                friendlyIdealDomain: friendlyIdealDomain,
                friendlyCity: friendlyCity,
            };

            await authApi.post("admin/generate-company-profile", payload);

            // Close dialog and refresh data
            setShowGenerateProfileDialog(false);
            setSelectedCompany(null);
            refetch();

            setImportMessage("Profile generated successfully!");
            setImportMessageType("success");
        } catch (error) {
            setImportMessage("Failed to generate profile: " + (error?.response?.data?.msg || error.message));
            setImportMessageType("danger");
        } finally {
            setIsGeneratingProfile(false);
        }
    };

    const hideGenerateProfileDialog = () => {
        setShowGenerateProfileDialog(false);
        setSelectedCompany(null);
        setFriendlyName('');
        setIdealDomainLC('');
        setFriendlyIdealDomain('');
        setFriendlyCity('');
        setDomainSearchResult(null);
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions">
                <Button
                    icon="pi pi-bars"
                    className="p-button-rounded p-button-icon-only p-button-text text-800"
                    onClick={(e) => showActionMenu(e, rowData)}
                    aria-controls="popup_menu"
                    aria-haspopup
                    tooltip="More Actions"
                    tooltipOptions={{ showDelay: 1000 }}
                />
            </div>
        );
    };

    const getProviderIdFromVanityName = async (vanityName) => {
        try {
            const searchBody = {
                first: 0,
                rows: 100,
                sortField: "createdAt",
                sortOrder: -1
            };

            const result = await authApi.post("admin/profile/list", searchBody);

            if (result.data?.profiles && result.data.profiles.length > 0) {
                const matchingProfile = result.data.profiles.find(
                    profile => profile.vanityName &&
                        profile.vanityName.toLowerCase() === vanityName.toLowerCase()
                );

                if (matchingProfile) {
                    return matchingProfile.providerId;
                }
            }
            return null;
        } catch (error) {
            console.error("Error getting providerId from vanityName:", error);
            return null;
        }
    };

    const switchProviderAndOpenProfile = async (providerId, vanityName, profileUrl, documentNumber) => {
        const originalCursor = document.body.style.cursor;
        try {
            document.body.style.cursor = 'wait';
            setOpeningProfileFor(documentNumber);

            let actualProviderId = providerId;
            if (!actualProviderId && vanityName) {
                actualProviderId = await getProviderIdFromVanityName(vanityName);
            }

            if (!actualProviderId) {
                console.warn("No providerId available, opening profile without switching");
                const fullUrl = profileUrl.startsWith('http')
                    ? profileUrl
                    : process.env.REACT_APP_NETWORK_SITE + "/company/" + vanityName;
                window.open(fullUrl, '_blank');
                return;
            }

            const response = await authApi.post("admin/switch-unclaimed-provider", {
                providerId: actualProviderId
            });

            if (response.data.userAccessToken) {
                TokenService.setAccessToken(response.data.userAccessToken);
            }
            if (response.data.userRefreshToken) {
                TokenService.setRefreshToken(response.data.userRefreshToken);
            }

            let fullUrl;
            if (profileUrl.startsWith('http')) {
                fullUrl = profileUrl;
            } else {
                fullUrl = process.env.REACT_APP_NETWORK_SITE + "/company/" + vanityName;
            }

            window.open(fullUrl, '_blank');
        } catch (error) {
            console.error("Error switching provider:", error);
            const fullUrl = profileUrl.startsWith('http')
                ? profileUrl
                : process.env.REACT_APP_NETWORK_SITE + "/company/" + vanityName;
            window.open(fullUrl, '_blank');
        } finally {
            document.body.style.cursor = originalCursor || 'default';
            setOpeningProfileFor(null);
        }
    };

    const companyNameBodyTemplate = (rowData) => {
        const hasProfileUrl = rowData.companyMetaData?.profileUrl;
        const isOpening = openingProfileFor === rowData.documentNumber;

        if (hasProfileUrl) {
            return (
                <a
                    href="#"
                    onClick={async (e) => {
                        e.preventDefault();
                        const profileUrl = rowData.companyMetaData.profileUrl;
                        // Extract vanity name from URL (e.g., /company/vanityName or full URL)
                        let vanityName = null;
                        if (profileUrl.includes('/company/')) {
                            const vanityMatch = profileUrl.match(/\/company\/([^\/\?]+)/);
                            vanityName = vanityMatch ? vanityMatch[1] : null;
                        } else {
                            // If it's just the vanity name
                            vanityName = profileUrl;
                        }

                        const providerId = rowData.companyMetaData?.providerId;

                        if (vanityName) {
                            await switchProviderAndOpenProfile(providerId, vanityName, profileUrl, rowData.documentNumber);
                        }
                    }}
                    style={{
                        color: 'inherit',
                        textDecoration: 'underline',
                        cursor: isOpening ? 'wait' : 'pointer'
                    }}
                >
                    {rowData.companyName}
                </a>
            );
        }

        return rowData.companyName;
    };

    return (
        <div>
            {pageHeader}
            <BlockUI blocked={isImporting} template={
                <div className="flex align-items-center justify-content-center" style={{ width: '100%', height: '100%' }}>
                    <ProgressSpinner />
                </div>
            }>
                <div id="ImportCompanyDataForm" className="card mb-3">
                    <div className="formgrid grid p-fluid">
                        <div className="field ">
                            <label htmlFor="stateSelect" className="font-bold block mb-2 ml-1">
                                State
                            </label>
                            <Dropdown
                                id="stateSelect"
                                value={stateSelection || null}
                                onChange={e => setStateSelection(e.value)}
                                options={[
                                    { label: "Florida", value: "FL" }
                                ]}
                                className="w-full"
                                placeholder="Select State"
                            />
                        </div>
                        <div className="field ml-3">
                            <label htmlFor="fileUpload" className="font-bold block mb-2">
                                &nbsp;
                            </label>
                            <FileUpload
                                ref={fileUploadRef}
                                id="fileUpload"
                                name="file"
                                accept=".txt"
                                mode="basic"
                                uploadHandler={handleImport}
                                customUpload
                                className="w-full"
                                chooseLabel="Import File"
                                auto={true}
                                maxFileSize={5000000}

                            />
                        </div>

                        {importMessage && (
                            <div className={`col-12 mt-3 alert ${importMessageType === 'success' ? 'alert-success' : 'alert-danger'}`}>
                                {importMessage}
                            </div>
                        )}
                    </div>
                </div>
                <div className="card mb-3">

                    {isError && (
                        <PageLoadError />
                    )}
                    {!isError && (
                        <>
                            <div className="flex flex-row flex-wrap align-items-center mb-3">
                                <div className="field mb-3">
                                    <span className="">
                                        <label htmlFor="searchText" className="mr-2">Company Name</label>
                                        <input
                                            id="searchText"
                                            type="text"
                                            className="p-inputtext p-component"
                                            placeholder="Search..."
                                            value={searchText || ''}
                                            onChange={updateSearchText}
                                        />
                                    </span>
                                </div>

                                {searchText && (
                                    <div className="field mb-3">
                                        <Button
                                            type="button"
                                            className="p-button-text"
                                            icon="pi pi-times"
                                            tooltip="Clear Filter"
                                            onClick={clearSearchText}
                                        />
                                    </div>
                                )}

                                <div className="field-checkbox ml-3">
                                    <Checkbox
                                        inputId="useKeywordSearch"
                                        checked={useKeywordSearch || false}
                                        onChange={updateUseKeywordSearch}
                                    />
                                    <label for="useKeywordSearch" className="ml-2">Filter on Keywords</label>
                                </div>
                            </div>



                            <DataTable
                                value={companyData}
                                paginator
                                rows={lazyState.rows}
                                rowsPerPageOptions={[5, 25, 50, 100, 500]}
                                className="p-datatable-sm"
                                loading={isLoading}
                                totalRecords={totalRecords}
                                onPage={onPage}
                                onSort={onSort}
                                sortField={lazyState.sortField}
                                sortOrder={lazyState.sortOrder}
                                lazy
                                first={lazyState.first}
                            >
                                <Column
                                    header="Actions"
                                    body={actionBodyTemplate}
                                    style={{ width: '80px' }}
                                />
                                <Column field="companyMetaData.registeredState" sortable header="Registered State" />
                                <Column
                                    field="companyName"
                                    sortable
                                    header="Company Name"
                                    body={companyNameBodyTemplate}
                                />
                                <Column field="address1" header="Address" body={addressBodyTemplate} />
                                <Column field="registeredAgentName" sortable header="Registered Agent Name" />
                                <Column
                                    field="filedOnDate"
                                    sortable
                                    header="Filed On Date"
                                    body={filedOnDateBodyTemplate}
                                />
                            </DataTable>
                            <Menu model={rowActions} popup ref={menu} className="white-space-nowrap" />
                        </>
                    )}
                </div>

            </BlockUI>

            <Dialog
                header="Generate Company Profile"
                visible={showGenerateProfileDialog}
                style={{ width: '50vw' }}
                onHide={hideGenerateProfileDialog}
                footer={
                    <div>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            onClick={hideGenerateProfileDialog}
                            className="p-button-text p-button-danger"
                        />
                        <Button
                            label="Generate"
                            icon="pi pi-check"
                            onClick={handleGenerateProfile}
                            disabled={isGeneratingProfile || !friendlyName || !idealDomainLC || !friendlyCity}
                            loading={isGeneratingProfile}
                        />
                    </div>
                }
            >
                <div className="formgrid grid p-fluid">
                    <div className="field col-12">
                        <label htmlFor="friendlyName" className="font-bold block mb-2">
                            Friendly Company Name
                        </label>
                        <InputText
                            id="friendlyName"
                            value={friendlyName}
                            onChange={(e) => setFriendlyName(e.target.value)}
                            className="w-full"
                        />
                        <small className="text-gray-600">Based on: {selectedCompany?.companyName}</small>
                    </div>

                    <div className="field col-12">
                        <label htmlFor="idealDomainLC" className="font-bold block mb-2">
                            Ideal Domain (lowercase)
                        </label>
                        <div className="flex align-items-center">
                            <InputText
                                id="idealDomainLC"
                                value={idealDomainLC}
                                onChange={(e) => {
                                    setIdealDomainLC(e.target.value);
                                    // Update friendly domain when ideal domain changes
                                    updateFriendlyDomainFromIdealDomain(e.target.value, friendlyName);
                                }}
                                className="w-full mr-2"
                            />
                            <Button
                                icon="pi pi-search"
                                onClick={handleDomainSearch}
                                loading={isCheckingDomain}
                                disabled={!idealDomainLC || isCheckingDomain}
                                tooltip="Check Availability"
                            />
                        </div>
                        {domainSearchResult && (
                            <div className={`mt-2 ${domainSearchResult.available ? 'text-green-600' : 'text-red-600'}`}>
                                {domainSearchResult.available ? (
                                    <><i className="pi pi-check mr-1"></i><span>Domain is available</span></>
                                ) : (
                                    <>
                                        <i className="pi pi-times mr-1"></i><span>Domain is not available{domainSearchResult.info?.suggestions?.length > 0 ? '. Try: ' + domainSearchResult.info.suggestions.slice(0, 3).map(s => s.domainName).join(', ') : ''}</span>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="field col-12">
                        <label htmlFor="friendlyIdealDomain" className="font-bold block mb-2">
                            Friendly Domain (for emails/postcards)
                        </label>
                        <InputText
                            id="friendlyIdealDomain"
                            value={friendlyIdealDomain}
                            onChange={(e) => setFriendlyIdealDomain(e.target.value)}
                            className="w-full"
                        />
                        <small className="text-gray-600">Format: www.CompanyName.com</small>
                    </div>

                    <div className="field col-12">
                        <label htmlFor="friendlyCity" className="font-bold block mb-2">
                            Friendly City Name
                        </label>
                        <InputText
                            id="friendlyCity"
                            value={friendlyCity}
                            onChange={(e) => setFriendlyCity(e.target.value)}
                            className="w-full"
                        />
                        <small className="text-gray-600">Based on: {selectedCompany?.city}</small>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default ImportCompanyData;
